"use client";

import axios from "axios";
import {
  Clock3,
  LogOut,
  Mic,
  MicOff,
  RotateCcw,
  Sparkles,
  Zap,
} from "lucide-react";
import { useRouter } from "next/navigation";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useAuth } from "@/lib/auth";
import {
  answerLimitForInterviewerMode,
  timeLimitSecondsForInterviewerMode,
  completeQuickInterview,
  createQuickInterviewSession,
  DEFAULT_ANSWER_LIMIT,
  DEFAULT_INTERVIEWER_MODE,
  DEFAULT_TIME_LIMIT_SECONDS,
  INTERVIEWER_MODE_OPTIONS,
  quitQuickInterview,
  transcribeQuickInterviewTurn,
  type QuickInterviewerMode,
  type QuickInterviewCompletionReason,
  type QuickInterviewTurn,
} from "@/lib/quick-interview/api";
import {
  CANDIDATE_TRANSCRIPT_FALLBACK,
  INTERVIEWER_TRANSCRIPT_FALLBACK,
  RealtimeConversationTracker,
  TRANSCRIPTION_RETRY_NOTICE,
} from "@/lib/quick-interview/realtimeEvents";
import {
  normalizeForMatching,
  sanitizeCandidateTranscript,
} from "@/lib/quick-interview/transcriptGuard";
import { QuickRealtimeSession } from "@/lib/quick-interview/realtimeSession";
import {
  WhisperAudioRecorder,
  type WhisperAudioMetrics,
} from "@/lib/quick-interview/whisperAudioRecorder";
import PageTitleHeader from "@/components/shared/PageTitleHeader";
import VoiceConversationStatus, {
  type QuickInterviewPhase,
} from "./VoiceConversationStatus";
import InterviewerVideoPanel from "./InterviewerVideoPanel";

const TRANSCRIPT_DRAIN_MS = 5_000;
const TRANSCRIPT_WATCHDOG_MS = 10_000;
// 締めの生成が完了してから音声が鳴り出すまでの猶予。ここを過ぎても
// 無音のままなら、音声が届かないものとして面接を終える
const CLOSING_AUDIO_GRACE_MS = 3_000;
// 締めの発話が始まった後、この時間だけ無音が続いたら再生終了とみなす
const CLOSING_SILENCE_MS = 900;
// 音量監視が使えない環境や応答が届かない障害でも面接を終わらせる保険
const CLOSING_MAX_WAIT_MS = 45_000;
// 面接官が発話中と判定する音量。環境ノイズは含まれない出力音声のみを見る
const CLOSING_SPEAKING_LEVEL = 0.02;
// 面接官が話し終えてから、応募者が話し始めもせず次の応答も来ないまま
// この時間が過ぎたら、面接は進まなくなったとみなして終了へ進む。
// 締めの文言を言い換えられて検知できなかった場合の最終防衛線で、
// 10分の時間切れまで固まった画面を見せないためにある
const INTERVIEWER_IDLE_TIMEOUT_MS = 90_000;
// 会話ログの自動追従を続ける「最下部からの距離」。ユーザーのスクロール操作時にだけ
// 判定するため、発話が追加されて伸びた分は距離に含まれない。末尾に留まっているかを
// 見るだけなので、指が少し動いた程度で非追従に落ちない範囲に収めている
const TRANSCRIPT_FOLLOW_THRESHOLD_PX = 48;
const NO_ANSWER_NOTICE =
  "採点できる回答がありませんでした。もう一度お試しください。";

interface PendingCompletion {
  sessionId: string;
  turns: QuickInterviewTurn[];
  reason: QuickInterviewCompletionReason;
}

function formatElapsed(seconds: number) {
  const minutes = Math.floor(seconds / 60)
    .toString()
    .padStart(2, "0");
  const remainder = (seconds % 60).toString().padStart(2, "0");
  return `${minutes}:${remainder}`;
}

function isMicrophonePermissionError(error: unknown) {
  return (
    error instanceof DOMException &&
    ["NotAllowedError", "PermissionDeniedError"].includes(error.name)
  );
}

function limitTurnsToAnswers(
  turns: QuickInterviewTurn[],
  answerLimit: number,
) {
  let candidateCount = 0;
  const limitIndex = turns.findIndex((turn) => {
    if (turn.role === "candidate") candidateCount += 1;
    return candidateCount === answerLimit;
  });
  if (limitIndex < 0) return turns;

  const trailingTurns = turns.slice(limitIndex + 1);
  const includesClosingTurn =
    trailingTurns[0]?.role === "interviewer" &&
    !trailingTurns.some((turn) => turn.role === "candidate");
  const endIndex = limitIndex + (includesClosingTurn ? 2 : 1);
  return turns.slice(0, endIndex).map((turn, position) => ({
    ...turn,
    position,
  }));
}

function appendClosingTurn(
  answerLimitTurns: QuickInterviewTurn[],
  currentTurns: QuickInterviewTurn[],
) {
  const currentInterviewerTurns = new Map(
    currentTurns
      .filter((turn) => turn.role === "interviewer")
      .map((turn) => [turn.provider_item_id, turn]),
  );
  const reconciledAnswerLimitTurns = answerLimitTurns.map((turn, position) => {
    const currentTurn = currentInterviewerTurns.get(turn.provider_item_id);
    return {
      ...(currentTurn ?? turn),
      position,
    };
  });

  if (reconciledAnswerLimitTurns.at(-1)?.role === "interviewer") {
    return reconciledAnswerLimitTurns;
  }

  const trailingTurns = currentTurns.slice(reconciledAnswerLimitTurns.length);
  const closingTurn =
    trailingTurns.find(
      (turn) =>
        turn.role === "interviewer" &&
        turn.transcript !== INTERVIEWER_TRANSCRIPT_FALLBACK,
    ) ?? null;

  return [
    ...reconciledAnswerLimitTurns,
    ...(closingTurn ? [closingTurn] : []),
  ].map((turn, position) => ({ ...turn, position }));
}

// 面接官の発話が締めかどうかを判定する。判定に使う文言はサーバーから受け取り、
// 面接官へ指示する締めの文言と同じ出どころにする。記号や空白の違いで
// 取りこぼさないよう、幻覚除去と同じ正規化を通してから照合する
function isClosingStatement(
  transcript: string,
  closingStatement: string | null,
) {
  if (!closingStatement) return false;

  const normalizedStatement = normalizeForMatching(closingStatement);
  if (normalizedStatement.length === 0) return false;

  return normalizeForMatching(transcript).includes(normalizedStatement);
}

// 締めの発話までを保存対象として切り出す。締めより後ろに並んだ発話は、
// 面接が終わってからの独り言なので回答として扱わない
function turnsUpToClosing(
  turns: QuickInterviewTurn[],
  closingItemId: string | undefined,
) {
  const closingIndex = turns.findIndex(
    (turn) => turn.provider_item_id === closingItemId,
  );
  if (closingIndex < 0) return turns;

  return turns
    .slice(0, closingIndex + 1)
    .map((turn, position) => ({ ...turn, position }));
}

function hasScorableCandidateTurns(turns: QuickInterviewTurn[]) {
  return turns.some(
    (turn) =>
      turn.role === "candidate" &&
      turn.transcript !== CANDIDATE_TRANSCRIPT_FALLBACK,
  );
}

function itemIdFromRealtimeEvent(rawEvent: string) {
  try {
    const event = JSON.parse(rawEvent) as { item_id?: unknown };
    return typeof event.item_id === "string" ? event.item_id : null;
  } catch {
    return null;
  }
}

function reconcileFrozenCandidateTurn(
  frozenTurns: QuickInterviewTurn[],
  itemId: string,
  transcript: string,
) {
  return frozenTurns.map((turn) =>
    turn.provider_item_id === itemId && turn.role === "candidate"
      ? { ...turn, transcript: replaceFirstCandidateSegment(turn, transcript) }
      : turn,
  );
}

function appendFrozenCandidateSegment(
  frozenTurns: QuickInterviewTurn[],
  mergedTurnKey: string,
  transcript: string,
) {
  return frozenTurns.map((turn) =>
    turn.provider_item_id === mergedTurnKey && turn.role === "candidate"
      ? { ...turn, transcript: `${turn.transcript}\n${transcript}` }
      : turn,
  );
}

function replaceFirstCandidateSegment(
  turn: QuickInterviewTurn,
  transcript: string,
) {
  const [, ...remainingSegments] = turn.transcript.split("\n");
  if (remainingSegments.length === 0) return transcript;

  return [transcript, ...remainingSegments].join("\n");
}

function stripCandidateFallbackSegments(transcript: string) {
  return transcript
    .split("\n")
    .filter((segment) => segment !== CANDIDATE_TRANSCRIPT_FALLBACK)
    .join("\n");
}

function normalizeTurnsForCompletion(turns: QuickInterviewTurn[]) {
  const filteredTurns = turns.flatMap((turn) => {
    if (turn.role !== "candidate") return [turn];

    const transcript = stripCandidateFallbackSegments(turn.transcript);
    return transcript ? [{ ...turn, transcript }] : [];
  });
  const alternatingTurns = filteredTurns.reduce<QuickInterviewTurn[]>(
    (result, turn) => {
      if (result.length === 0) {
        if (turn.role === "interviewer") result.push(turn);
        return result;
      }

      const previous = result.at(-1);
      if (previous?.role === turn.role) {
        if (turn.role === "interviewer") result[result.length - 1] = turn;
        return result;
      }

      result.push(turn);
      return result;
    },
    [],
  );

  return alternatingTurns.map((turn, position) => ({
    ...turn,
    position,
  }));
}

export default function QuickInterviewClient() {
  const { user, loading: authLoading } = useAuth();
  const router = useRouter();
  const [phase, setPhase] = useState<QuickInterviewPhase>("ready");
  const [turns, setTurns] = useState<QuickInterviewTurn[]>([]);
  const [answerLimit, setAnswerLimit] = useState(DEFAULT_ANSWER_LIMIT);
  const [timeLimitSeconds, setTimeLimitSeconds] = useState(
    DEFAULT_TIME_LIMIT_SECONDS,
  );
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [inputLevel, setInputLevel] = useState(0);
  const [showEndDialog, setShowEndDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [isClosingAfterLimit, setIsClosingAfterLimit] = useState(false);
  const [pendingCompletion, setPendingCompletion] =
    useState<PendingCompletion | null>(null);
  // 面接官モードは開始前にだけ選ぶ（#1728）。開始後の変更は面接の途中で面接官の姿勢が
  // 変わることになるため受け付けない
  const [interviewerMode, setInterviewerMode] = useState<QuickInterviewerMode>(
    DEFAULT_INTERVIEWER_MODE,
  );

  const realtimeRef = useRef<QuickRealtimeSession | null>(null);
  const whisperRecorderRef = useRef<WhisperAudioRecorder | null>(null);
  const whisperTurnSequenceRef = useRef(0);
  const trackerRef = useRef(new RealtimeConversationTracker());
  const sessionIdRef = useRef<string | null>(null);
  const answerLimitRef = useRef(DEFAULT_ANSWER_LIMIT);
  const startedAtRef = useRef<number | null>(null);
  const settledRef = useRef(false);
  const endingRef = useRef(false);
  const closingAfterLimitRef = useRef(false);
  const closingSpeakingSeenRef = useRef(false);
  const closingInstructionsRef = useRef<string | undefined>(undefined);
  const closingMaxWaitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const answerLimitTurnsRef = useRef<QuickInterviewTurn[] | null>(null);
  const answerLimitPendingIdsRef = useRef<Set<string>>(new Set());
  const closingGraceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const closingResponseIdRef = useRef<string | null>(null);
  const closingResponseStartedRef = useRef(false);
  const closingStatementRef = useRef<string | null>(null);
  const interviewerIdleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  // 回答が上限に達する前に面接官が締めた場合を、上限に達して終わった場合と
  // 区別して記録する
  const interviewerEndedRef = useRef(false);
  const disconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const closingPlaybackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const transcriptWatchdogRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const candidateSpeakingRef = useRef(false);
  const pendingResponseRef = useRef(false);
  const responseRequestedRef = useRef(false);
  const closingResponseRequestedRef = useRef(false);
  const transcriptContainerRef = useRef<HTMLDivElement | null>(null);
  const isTranscriptFollowingRef = useRef(true);
  const lastTranscriptScrollTopRef = useRef(0);
  const finishRef = useRef<
    (reason: QuickInterviewCompletionReason) => Promise<void>
  >(async () => {});

  // 締めを待って終わる経路の終了理由。回答が上限に達したのか、面接官が
  // その前に締めたのかで分ける
  const closingCompletionReason =
    useCallback((): QuickInterviewCompletionReason => {
      return interviewerEndedRef.current ? "interviewer_ended" : "answer_limit";
    }, []);

  const clearInterviewerIdleWatchdog = useCallback(() => {
    if (interviewerIdleTimerRef.current) {
      clearTimeout(interviewerIdleTimerRef.current);
      interviewerIdleTimerRef.current = null;
    }
  }, []);

  // 面接官が話し終えた後、応募者が話し始めもせず次の応答も来ない状態を見張る。
  // 締めの文言を言い換えられて検知できなかった場合でも、時間切れを待たずに
  // 結果へ進めるようにする
  const startInterviewerIdleWatchdog = useCallback(() => {
    clearInterviewerIdleWatchdog();
    interviewerIdleTimerRef.current = setTimeout(() => {
      interviewerIdleTimerRef.current = null;
      if (endingRef.current || closingAfterLimitRef.current) return;
      interviewerEndedRef.current = true;
      finishRef.current("interviewer_ended");
    }, INTERVIEWER_IDLE_TIMEOUT_MS);
  }, [clearInterviewerIdleWatchdog]);

  const clearTranscriptWatchdog = useCallback(() => {
    if (transcriptWatchdogRef.current) {
      clearTimeout(transcriptWatchdogRef.current);
      transcriptWatchdogRef.current = null;
    }
  }, []);

  const clearRealtimeTimers = useCallback(() => {
    if (closingGraceTimerRef.current) {
      clearTimeout(closingGraceTimerRef.current);
      closingGraceTimerRef.current = null;
    }
    if (disconnectTimerRef.current) {
      clearTimeout(disconnectTimerRef.current);
      disconnectTimerRef.current = null;
    }
    if (closingPlaybackTimerRef.current) {
      clearTimeout(closingPlaybackTimerRef.current);
      closingPlaybackTimerRef.current = null;
    }
    if (closingMaxWaitTimerRef.current) {
      clearTimeout(closingMaxWaitTimerRef.current);
      closingMaxWaitTimerRef.current = null;
    }
    clearTranscriptWatchdog();
    clearInterviewerIdleWatchdog();
  }, [clearInterviewerIdleWatchdog, clearTranscriptWatchdog]);

  // 面接官の応答開始はクライアントが指示する。ユーザーが発話を再開している、
  // 応答実行中、または応答開始を送信済みで開始確認待ちのときは送信せず保留し、
  // 次の契機（文字起こし完了・応答完了・回復処理）で再判定する。
  // 応答実行中の重複指示はAPIエラーになるため同じ条件で防ぐ
  const requestInterviewerResponse = useCallback(
    (responseInProgress: boolean) => {
      if (
        candidateSpeakingRef.current ||
        responseInProgress ||
        responseRequestedRef.current
      ) {
        pendingResponseRef.current = true;
        return false;
      }
      const sent = realtimeRef.current?.createResponse() ?? false;
      if (!sent) {
        pendingResponseRef.current = true;
        return false;
      }
      pendingResponseRef.current = false;
      responseRequestedRef.current = true;
      return true;
    },
    [],
  );

  // 文字起こしが回答にならなかったとき（無効判定・失敗・無応答）の共通回復:
  // 保留中の応答指示があれば送信を再判定し、送信しないなら回答待ち表示へ戻す
  const recoverAwaitingAnswer = useCallback(
    (responseInProgress: boolean) => {
      const responded = pendingResponseRef.current
        ? requestInterviewerResponse(responseInProgress)
        : false;
      if (
        !responded &&
        !responseInProgress &&
        !candidateSpeakingRef.current
      ) {
        setPhase("candidate_waiting");
      }
    },
    [requestInterviewerResponse],
  );

  // 応答の自動開始を無効化しているため、文字起こしイベントが届かないと
  // 面接が無言のまま止まる。発話停止後に一定時間文字起こしが来なければ、
  // 既存の再回答案内に合流させて回答待ちへ戻す
  const startTranscriptWatchdog = useCallback(() => {
    clearTranscriptWatchdog();
    transcriptWatchdogRef.current = setTimeout(() => {
      transcriptWatchdogRef.current = null;
      if (endingRef.current || closingAfterLimitRef.current) return;
      setNotice(TRANSCRIPTION_RETRY_NOTICE);
      recoverAwaitingAnswer(
        trackerRef.current.snapshot().responseInProgress,
      );
    }, TRANSCRIPT_WATCHDOG_MS);
  }, [clearTranscriptWatchdog, recoverAwaitingAnswer]);

  // 締めの生成が完了した時点から、音声が鳴り出すのを待つ。
  // 要求した時刻ではなく完了した時刻を起点にするため、生成に時間がかかっても
  // 待ち損ねない
  const scheduleClosingAudioGrace = useCallback(() => {
    if (closingSpeakingSeenRef.current || closingGraceTimerRef.current) return;

    closingGraceTimerRef.current = setTimeout(() => {
      closingGraceTimerRef.current = null;
      finishRef.current(closingCompletionReason());
    }, CLOSING_AUDIO_GRACE_MS);
  }, [closingCompletionReason]);

  // 面接官の音声を実測して締めの再生完了を判定する。文字数からの見積もりは
  // 読み上げ速度や言い回しでずれるため、発話を検知したうえで無音の継続で終える
  // 締めの応答が始まる前に鳴っている音声は、回答上限に達した時点で実行中
  // だった別の応答の残りである。要求済みかどうかだけで判定すると、
  // その応答の残り音を締めと誤認して締めを言う前に面接が終わるため、
  // 締めの応答が実際に始まったことを確認してから音量を見る
  const handleClosingOutputLevel = useCallback((level: number) => {
    if (
      !closingAfterLimitRef.current ||
      !closingResponseStartedRef.current ||
      endingRef.current
    ) {
      return;
    }

    if (level > CLOSING_SPEAKING_LEVEL) {
      closingSpeakingSeenRef.current = true;
      if (closingGraceTimerRef.current) {
        clearTimeout(closingGraceTimerRef.current);
        closingGraceTimerRef.current = null;
      }
      if (closingPlaybackTimerRef.current) {
        clearTimeout(closingPlaybackTimerRef.current);
        closingPlaybackTimerRef.current = null;
      }
      return;
    }

    if (!closingSpeakingSeenRef.current || closingPlaybackTimerRef.current) {
      return;
    }
    closingPlaybackTimerRef.current = setTimeout(() => {
      closingPlaybackTimerRef.current = null;
      finishRef.current(closingCompletionReason());
    }, CLOSING_SILENCE_MS);
  }, [closingCompletionReason]);

  // 締めの応答を要求する。応答の自動開始を無効化しているためクライアントが
  // 要求し、モデルの回答回数カウントに依存せず締め文言を話させる指示を渡す。
  // 送信できなかった場合は接続が失われているので、待っても何も届かない。
  // 無音のまま保険タイマーを待たせず、その場で終了へ進む
  const requestClosingResponse = useCallback(() => {
    closingResponseIdRef.current = null;
    closingResponseStartedRef.current = false;
    closingSpeakingSeenRef.current = false;
    const sent =
      realtimeRef.current?.createResponse(closingInstructionsRef.current) ??
      false;
    if (!sent) {
      finishRef.current(closingCompletionReason());
      return;
    }
    closingResponseRequestedRef.current = true;
    responseRequestedRef.current = true;
  }, [closingCompletionReason]);

  const scheduleClosingMaxWait = useCallback(() => {
    if (closingMaxWaitTimerRef.current) {
      clearTimeout(closingMaxWaitTimerRef.current);
    }
    closingMaxWaitTimerRef.current = setTimeout(() => {
      closingMaxWaitTimerRef.current = null;
      finishRef.current(closingCompletionReason());
    }, CLOSING_MAX_WAIT_MS);
  }, [closingCompletionReason]);

  const waitForPendingTranscript = useCallback(async () => {
    const deadline = Date.now() + TRANSCRIPT_DRAIN_MS;
    while (
      trackerRef.current.hasPendingCandidateTranscripts() &&
      Date.now() < deadline
    ) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  }, []);

  const finishInterview = useCallback(
    async (reason: QuickInterviewCompletionReason) => {
      const sessionId = sessionIdRef.current;
      if (!sessionId || endingRef.current) return;

      endingRef.current = true;
      clearRealtimeTimers();
      startedAtRef.current = null;
      setShowEndDialog(false);
      setPhase("ending");
      setNotice(null);
      whisperRecorderRef.current?.stop();
      whisperRecorderRef.current = null;
      realtimeRef.current?.stopInput();
      // 締めの発話が再生中に打ち切ると途中で切れるため、締めを待って
      // 終わる経路では応答を打ち切らない
      if (reason !== "answer_limit" && reason !== "interviewer_ended") {
        realtimeRef.current?.cancelResponse();
      }

      await waitForPendingTranscript();
      const currentTurns = trackerRef.current.snapshot({
        includePendingCandidateFallbacks: true,
      }).turns;
      const finalTurns = normalizeTurnsForCompletion(
        answerLimitTurnsRef.current
          ? appendClosingTurn(answerLimitTurnsRef.current, currentTurns)
          : limitTurnsToAnswers(currentTurns, answerLimitRef.current),
      );
      realtimeRef.current?.close();
      realtimeRef.current = null;

      if (!hasScorableCandidateTurns(finalTurns)) {
        await quitQuickInterview(sessionId).catch(() => {});
        settledRef.current = true;
        sessionIdRef.current = null;
        trackerRef.current = new RealtimeConversationTracker();
        startedAtRef.current = null;
        setTurns([]);
        setElapsedSeconds(0);
        setPhase("ready");
        setErrorMessage(null);
        // 無言で開始画面へ戻ると面接が突然消えたように見えるため理由を伝える
        setNotice(NO_ANSWER_NOTICE);
        endingRef.current = false;
        return;
      }

      const completion = { sessionId, turns: finalTurns, reason };
      setPendingCompletion(completion);
      try {
        await completeQuickInterview(sessionId, {
          turns: finalTurns,
          completionReason: reason,
        });
        settledRef.current = true;
        router.push(`/quick-interview/feedback/${sessionId}`);
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 503) {
          settledRef.current = true;
          router.push(`/quick-interview/feedback/${sessionId}`);
          return;
        }
        setErrorMessage(
          "会話の保存を完了できませんでした。接続を確認して再試行してください。",
        );
        setPhase("error");
        endingRef.current = false;
      }
    },
    [clearRealtimeTimers, router, waitForPendingTranscript],
  );

  useEffect(() => {
    finishRef.current = finishInterview;
  }, [finishInterview]);

  const handleWhisperSpeechStart = useCallback(() => {
    if (endingRef.current || closingAfterLimitRef.current) return;
    candidateSpeakingRef.current = true;
    clearInterviewerIdleWatchdog();
    clearTranscriptWatchdog();
    setNotice(null);
    setPhase("candidate_speaking");
  }, [clearInterviewerIdleWatchdog, clearTranscriptWatchdog]);

  const handleWhisperSpeechStop = useCallback(
    async (audio: Blob, metrics: WhisperAudioMetrics) => {
      candidateSpeakingRef.current = false;
      if (endingRef.current || closingAfterLimitRef.current) return;

      const sessionId = sessionIdRef.current;
      if (!sessionId) return;

      setPhase("candidate_checking");
      setNotice(null);
      try {
        const result = await transcribeQuickInterviewTurn(sessionId, audio, metrics);
        const transcript = sanitizeCandidateTranscript(result.transcript.trim());
        if (!transcript) {
          setNotice(TRANSCRIPTION_RETRY_NOTICE);
          setPhase("candidate_waiting");
          return;
        }

        whisperTurnSequenceRef.current += 1;
        const itemId = `whisper-${Date.now()}-${whisperTurnSequenceRef.current}`;
        const sent = realtimeRef.current?.createTextInput(itemId, transcript) ?? false;
        if (!sent) throw new Error("音声接続が終了しています。");
      } catch (error) {
        setNotice(
          axios.isAxiosError(error) && typeof error.response?.data?.error === "string"
            ? error.response.data.error
            : TRANSCRIPTION_RETRY_NOTICE,
        );
        setPhase("candidate_waiting");
      }
    },
    [],
  );

  const handleRealtimeEvent = useCallback((rawEvent: string) => {
    const update = trackerRef.current.handle(rawEvent);
    setTurns(update.turns);
    const reachedAnswerLimit =
      update.candidateCount >= answerLimitRef.current;
    if (
      reachedAnswerLimit &&
      !answerLimitTurnsRef.current
    ) {
      answerLimitTurnsRef.current = limitTurnsToAnswers(
        update.turns,
        answerLimitRef.current,
      );
      answerLimitPendingIdsRef.current = new Set(
        trackerRef.current.pendingCandidateItemIds(),
      );
    } else if (
      update.signal === "candidate_transcript" &&
      answerLimitTurnsRef.current
    ) {
      const itemId = itemIdFromRealtimeEvent(rawEvent);
      const transcript = itemId
        ? trackerRef.current.transcriptForItem(itemId)
        : null;
      if (itemId && transcript) {
        const frozenTurns = answerLimitTurnsRef.current;
        const isFrozenItem = frozenTurns.some(
          (turn) =>
            turn.role === "candidate" && turn.provider_item_id === itemId,
        );
        if (isFrozenItem) {
          answerLimitTurnsRef.current = reconcileFrozenCandidateTurn(
            frozenTurns,
            itemId,
            transcript,
          );
        } else if (answerLimitPendingIdsRef.current.has(itemId)) {
          const mergedTurnKey =
            trackerRef.current.mergedTurnKeyForItem(itemId);
          if (mergedTurnKey) {
            answerLimitTurnsRef.current = appendFrozenCandidateSegment(
              frozenTurns,
              mergedTurnKey,
              transcript,
            );
          }
        }
      }
    }
    if (endingRef.current) return;

    if (reachedAnswerLimit && !closingAfterLimitRef.current) {
      closingAfterLimitRef.current = true;
      setIsClosingAfterLimit(true);
      pendingResponseRef.current = false;
      whisperRecorderRef.current?.stop();
      realtimeRef.current?.stopInput();
      // 音量監視や応答が働かない場合でも面接が終わらない状態を作らない
      scheduleClosingMaxWait();
      if (!update.responseInProgress) requestClosingResponse();
    }

    switch (update.signal) {
      case "candidate_started":
        candidateSpeakingRef.current = true;
        clearInterviewerIdleWatchdog();
        clearTranscriptWatchdog();
        setPhase("candidate_speaking");
        break;
      case "candidate_stopped":
        candidateSpeakingRef.current = false;
        startTranscriptWatchdog();
        setPhase("candidate_checking");
        break;
      case "candidate_transcript":
        clearTranscriptWatchdog();
        setNotice(null);
        if (!closingAfterLimitRef.current) {
          requestInterviewerResponse(update.responseInProgress);
        }
        break;
      case "candidate_transcript_rejected":
        clearTranscriptWatchdog();
        if (update.itemId) realtimeRef.current?.deleteItem(update.itemId);
        if (closingAfterLimitRef.current) break;
        recoverAwaitingAnswer(update.responseInProgress);
        break;
      case "interviewer_transcript":
        // 面接官は回答が上限に達する前に締めることがある。回答数だけを
        // 終了条件にすると、その場合に終わらせる経路が無くなるため、
        // 締めの発話そのものも終了のきっかけとして拾う。
        // 会話の末尾ではなく、いま文字起こしが完了した面接官の発話だけを見る。
        // 締めの再生中に応募者が話すと末尾は応募者の発話になり、本物の締めを
        // 取りこぼす。逆に応募者が同じ文言を口にしても終了させない
        if (
          !closingAfterLimitRef.current &&
          isClosingStatement(
            (update.itemId &&
              trackerRef.current.transcriptForItem(update.itemId)) ||
              "",
            closingStatementRef.current,
          )
        ) {
          closingAfterLimitRef.current = true;
          setIsClosingAfterLimit(true);
          pendingResponseRef.current = false;
          // 締めはすでに面接官が話しているため、新たに応答は要求しない。
          // 鳴っている音声をそのまま締めとして扱う
          closingResponseRequestedRef.current = true;
          closingResponseStartedRef.current = true;
          closingResponseIdRef.current = null;
          closingSpeakingSeenRef.current = false;
          interviewerEndedRef.current = true;
          // 締めまでの会話を確定させる。締めの再生中に発した独り言が
          // 後から文字起こしされても、回答として採点されないようにする
          answerLimitTurnsRef.current = turnsUpToClosing(
            update.turns,
            update.itemId,
          );
          realtimeRef.current?.stopInput();
          scheduleClosingMaxWait();
          // 締めの音声がすでに鳴り終わっていた場合、音量監視も応答完了も
          // 起点にできない。回答上限の経路と同じ短い猶予で終える
          scheduleClosingAudioGrace();
        } else if (!closingAfterLimitRef.current) {
          startInterviewerIdleWatchdog();
        }
        break;
      case "interviewer_started":
        responseRequestedRef.current = false;
        setPhase("interviewer_speaking");
        // 締めとして要求した応答が始まったことを記録し、識別子を覚えておく。
        // ここを境に、鳴っている音声を締めの発話として扱う
        if (closingResponseRequestedRef.current) {
          closingResponseStartedRef.current = true;
          if (!closingResponseIdRef.current && update.responseId) {
            closingResponseIdRef.current = update.responseId;
          }
        }
        break;
      case "response_done":
        responseRequestedRef.current = false;
        if (closingAfterLimitRef.current) {
          // 回答上限へ応答実行中に到達した場合、その応答の終了時点で
          // 締めの応答をまだ要求していなければ一度だけ要求する
          if (!closingResponseRequestedRef.current) {
            requestClosingResponse();
          } else if (
            // 締めが始まる前に届いた完了通知は、直前の応答のものなので待つ。
            // どちら側の識別子も取れないことを理由に待ち続けはしない
            closingResponseStartedRef.current &&
            (!closingResponseIdRef.current ||
              !update.responseId ||
              update.responseId === closingResponseIdRef.current)
          ) {
            scheduleClosingAudioGrace();
          }
        } else {
          setPhase("candidate_waiting");
          if (pendingResponseRef.current) {
            // 直前の応答に面接官の発話があれば保留分は回答済みとして破棄し、
            // 割り込みで打ち切られた応答（発話なし）なら保留分を送信する
            if (update.turns.at(-1)?.role === "interviewer") {
              pendingResponseRef.current = false;
            } else {
              requestInterviewerResponse(false);
            }
          }
        }
        break;
      case "transcription_failed":
        clearTranscriptWatchdog();
        setNotice(update.errorMessage ?? TRANSCRIPTION_RETRY_NOTICE);
        if (!closingAfterLimitRef.current) {
          recoverAwaitingAnswer(update.responseInProgress);
        }
        break;
      case "error":
        setNotice(update.errorMessage ?? "音声接続でエラーが発生しました。");
        break;
    }
  }, [
    clearInterviewerIdleWatchdog,
    clearTranscriptWatchdog,
    recoverAwaitingAnswer,
    requestClosingResponse,
    requestInterviewerResponse,
    scheduleClosingMaxWait,
    scheduleClosingAudioGrace,
    startInterviewerIdleWatchdog,
    startTranscriptWatchdog,
  ]);

  const handleConnectionStateChange = useCallback(
    (connectionState: RTCPeerConnectionState) => {
      if (connectionState === "connected") {
        if (disconnectTimerRef.current) {
          clearTimeout(disconnectTimerRef.current);
          disconnectTimerRef.current = null;
        }
        return;
      }
      if (
        !["disconnected", "failed"].includes(connectionState) ||
        endingRef.current
      ) {
        return;
      }

      const handleDisconnect = () => {
        if (endingRef.current) return;
        const candidateCount = trackerRef.current.snapshot().candidateCount;
        if (candidateCount > 0) {
          finishRef.current("user_ended");
          return;
        }
        realtimeRef.current?.close();
        realtimeRef.current = null;
        whisperRecorderRef.current?.stop();
        whisperRecorderRef.current = null;
        const sessionId = sessionIdRef.current;
        if (sessionId) quitQuickInterview(sessionId).catch(() => {});
        settledRef.current = true;
        sessionIdRef.current = null;
        startedAtRef.current = null;
        setErrorMessage("音声接続が切断されました。もう一度開始してください。");
        setPhase("error");
      };

      if (connectionState === "failed") {
        handleDisconnect();
      } else if (!disconnectTimerRef.current) {
        disconnectTimerRef.current = setTimeout(handleDisconnect, 2_000);
      }
    },
    [],
  );

  const startInterview = useCallback(async () => {
    setErrorMessage(null);
    setNotice(null);
    setPendingCompletion(null);
    // 前回の面接で読み返していた場合、非追従のまま次の面接に持ち越さない
    isTranscriptFollowingRef.current = true;
    lastTranscriptScrollTopRef.current = 0;
    setTurns([]);
    setElapsedSeconds(0);
    setIsMuted(false);
    setInputLevel(0);
    startedAtRef.current = null;
    setPhase("requesting_permission");
    settledRef.current = false;
    endingRef.current = false;
    closingAfterLimitRef.current = false;
    setIsClosingAfterLimit(false);
    closingSpeakingSeenRef.current = false;
    closingInstructionsRef.current = undefined;
    closingStatementRef.current = null;
    interviewerEndedRef.current = false;
    answerLimitTurnsRef.current = null;
    answerLimitPendingIdsRef.current = new Set();
    candidateSpeakingRef.current = false;
    pendingResponseRef.current = false;
    responseRequestedRef.current = false;
    closingResponseRequestedRef.current = false;
    closingResponseIdRef.current = null;
    closingResponseStartedRef.current = false;
    trackerRef.current = new RealtimeConversationTracker();

    const realtime = new QuickRealtimeSession({
      onEvent: handleRealtimeEvent,
      onConnectionStateChange: handleConnectionStateChange,
      onInputLevel: setInputLevel,
      onOutputLevel: handleClosingOutputLevel,
    });
    realtimeRef.current = realtime;

    try {
      await realtime.prepareMedia();
      setPhase("connecting");
      const session = await createQuickInterviewSession(interviewerMode);
      sessionIdRef.current = session.id;
      trackerRef.current = new RealtimeConversationTracker({
        maxTurnTranscriptLength: session.turn_transcript_max_length,
      });
      answerLimitRef.current = session.answer_limit;
      closingInstructionsRef.current = session.closing_instructions;
      closingStatementRef.current = session.closing_statement ?? null;
      setAnswerLimit(session.answer_limit);
      setTimeLimitSeconds(session.time_limit_seconds);
      await realtime.connect(session.id, session.stt_mode);
      if (session.stt_mode === "whisper") {
        const stream = realtime.getLocalStream();
        if (!stream) throw new Error("マイクが初期化されていません。");
        const recorder = new WhisperAudioRecorder({
          stream,
          onSpeechStart: handleWhisperSpeechStart,
          onSpeechStop: handleWhisperSpeechStop,
        });
        whisperRecorderRef.current = recorder;
        recorder.start();
      }
      startedAtRef.current = Date.now();
      setPhase("interviewer_speaking");
      realtime.createResponse();
    } catch (error) {
      realtime.close();
      realtimeRef.current = null;
      const sessionId = sessionIdRef.current;
      if (sessionId) await quitQuickInterview(sessionId).catch(() => {});
      settledRef.current = true;
      sessionIdRef.current = null;
      startedAtRef.current = null;
      setErrorMessage(
        isMicrophonePermissionError(error)
          ? "クイック面接にはマイクの許可が必要です。ブラウザの設定を確認してください。"
          : error instanceof Error
            ? error.message
            : "音声接続に失敗しました。もう一度お試しください。",
      );
      setPhase("error");
    }
  }, [
    handleClosingOutputLevel,
    handleConnectionStateChange,
    handleRealtimeEvent,
    handleWhisperSpeechStart,
    handleWhisperSpeechStop,
    interviewerMode,
  ]);

  const retryCompletion = useCallback(async () => {
    if (!pendingCompletion) return;
    setPhase("ending");
    setErrorMessage(null);
    endingRef.current = true;
    try {
      await completeQuickInterview(pendingCompletion.sessionId, {
        turns: pendingCompletion.turns,
        completionReason: pendingCompletion.reason,
      });
      settledRef.current = true;
      router.push(
        `/quick-interview/feedback/${pendingCompletion.sessionId}`,
      );
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 503) {
        settledRef.current = true;
        router.push(
          `/quick-interview/feedback/${pendingCompletion.sessionId}`,
        );
        return;
      }
      endingRef.current = false;
      setErrorMessage("結果生成を再開できませんでした。もう一度お試しください。");
      setPhase("error");
    }
  }, [pendingCompletion, router]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) router.replace("/login");
  }, [authLoading, router, user]);

  useEffect(() => {
    if (!startedAtRef.current || endingRef.current) return;
    const interval = window.setInterval(() => {
      if (!startedAtRef.current || endingRef.current) return;
      const elapsed = Math.floor((Date.now() - startedAtRef.current) / 1_000);
      setElapsedSeconds(elapsed);
      if (elapsed >= timeLimitSeconds) finishRef.current("time_limit");
    }, 1_000);
    return () => window.clearInterval(interval);
  }, [phase, timeLimitSeconds]);

  // 追従を止めるのはユーザーが自分で上へスクロールしたときだけにする。
  // 発話追加では scroll イベントが起きないため、ここに記録された「末尾に
  // 留まっているか」は常に発話が増える前の状態を表す。末尾へ戻す自動
  // スクロールは下方向にしか動かないので、その途中経過で追従が切れることもない
  const handleTranscriptScroll = useCallback(() => {
    const container = transcriptContainerRef.current;
    if (!container) return;
    // iOS のバウンススクロールでは scrollTop が範囲外の値になり、
    // 端にいるだけなのに離れたと判定されてしまうため丸める
    const maxScrollTop = Math.max(
      0,
      container.scrollHeight - container.clientHeight,
    );
    const scrollTop = Math.min(maxScrollTop, Math.max(0, container.scrollTop));
    const scrolledUp = scrollTop < lastTranscriptScrollTopRef.current;
    lastTranscriptScrollTopRef.current = scrollTop;
    const distanceFromBottom = maxScrollTop - scrollTop;
    if (distanceFromBottom <= TRANSCRIPT_FOLLOW_THRESHOLD_PX) {
      isTranscriptFollowingRef.current = true;
    } else if (scrolledUp) {
      isTranscriptFollowingRef.current = false;
    }
  }, []);

  useEffect(() => {
    // scrollIntoView はスクロール可能な祖先（モバイルではページ全体）まで
    // 動かしてしまうため、ログ枠のコンテナ内だけをスクロールさせる。
    // 追従するかどうかは発話が追加される前のスクロール位置で決まっているので、
    // ここでは距離を測り直さない（長い吹き出しが1つ増えただけで追従が
    // 止まってしまうため）
    const container = transcriptContainerRef.current;
    if (!container || turns.length === 0) return;
    if (!isTranscriptFollowingRef.current) return;
    container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
  }, [turns]);

  useEffect(() => {
    return () => {
      clearRealtimeTimers();
      whisperRecorderRef.current?.stop();
      whisperRecorderRef.current = null;
      realtimeRef.current?.close();
      const sessionId = sessionIdRef.current;
      if (sessionId && !settledRef.current) {
        quitQuickInterview(sessionId).catch(() => {});
      }
    };
  }, [clearRealtimeTimers]);

  if (authLoading || !user) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-sm text-subtext">読み込み中...</p>
      </div>
    );
  }

  const answerCount = turns.filter((turn) => turn.role === "candidate").length;
  // 締めを待っている間に終了やミュートを押せると、締めの発話が途中で切れ、
  // 終了理由も実態と食い違う。回答数が上限に満たない締めもあるため、
  // 締め待ちに入ったかどうかを直接見る
  const isSessionActive =
    !["ready", "error", "ending"].includes(phase) &&
    !isClosingAfterLimit &&
    answerCount < answerLimit;
  return (
    <div className="min-h-screen">
      <PageTitleHeader
        imageSrc="/images/mentai-senpai_03_good01.png"
        imageAlt="めんたい先輩"
        title="クイック面接"
        subtitle="リアルタイム音声で、短く実践する面接練習"
        containerClassName="mx-auto max-w-7xl px-5 py-6 md:px-10 md:py-8"
      />

      <div className="mx-auto max-w-7xl px-5 py-6 md:px-10 md:py-8">
        {phase === "ready" ? (
          <section className="grid overflow-hidden rounded-lg bg-surface custom-shadow lg:grid-cols-[minmax(0,1.35fr)_minmax(320px,0.65fr)]">
            <div className="min-w-0">
              <InterviewerVideoPanel
                phase="ready"
                showLabel={false}
                sizeClassName="h-full min-h-[300px]"
              />
            </div>
            <div className="flex flex-col justify-center p-6 md:p-8">
              <div className="mb-5 flex items-center gap-2 text-sm font-bold text-primary">
                <Zap size={18} aria-hidden="true" />
                すぐに始まる音声面接
              </div>
              <h2 className="text-2xl font-bold text-on-surface">
                {/* 開始前は選んだ面接官モードの回答数と制限時間を出す。面接中はサーバーが返した値を使う */}
                {`${answerLimitForInterviewerMode(interviewerMode)}回答・最大${Math.round(
                  timeLimitSecondsForInterviewerMode(interviewerMode) / 60,
                )}分`}
              </h2>
              <dl className="mt-5 divide-y divide-border text-sm">
                <div className="flex items-center justify-between py-3">
                  <dt className="text-subtext">進行</dt>
                  <dd className="font-bold">自動で質問と深掘り</dd>
                </div>
                <div className="flex items-center justify-between py-3">
                  <dt className="text-subtext">操作</dt>
                  <dd className="font-bold">ハンズフリー</dd>
                </div>
                <div className="flex items-center justify-between py-3">
                  <dt className="text-subtext">終了後</dt>
                  <dd className="font-bold">5軸フィードバック</dd>
                </div>
              </dl>
              {/* 面接官モードの選択（#1728）。開始前だけ選べる */}
              <fieldset className="mt-5" data-testid="interviewer-mode-selector">
                <legend className="text-sm text-subtext">面接官の当たり</legend>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {INTERVIEWER_MODE_OPTIONS.map((option) => {
                    const isSelected = interviewerMode === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        aria-pressed={isSelected}
                        data-testid={`interviewer-mode-${option.value}`}
                        onClick={() => setInterviewerMode(option.value)}
                        className={`min-h-[44px] rounded-lg border px-2 py-2 text-sm font-bold transition-colors ${
                          isSelected
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-border text-subtext hover:border-primary/40"
                        }`}
                      >
                        <span className="block">{option.label}</span>
                        <span
                          className={`block text-[11px] font-normal ${
                            isSelected ? "text-primary/80" : "text-subtext"
                          }`}
                        >
                          {option.answerLimit}回答
                        </span>
                      </button>
                    );
                  })}
                </div>
                <p
                  className="mt-2 text-xs leading-relaxed text-subtext"
                  data-testid="interviewer-mode-description"
                >
                  {
                    INTERVIEWER_MODE_OPTIONS.find(
                      (option) => option.value === interviewerMode,
                    )?.description
                  }
                </p>
                {/* 当たりを変えても採点は同じ基準であることを明示する。厳しめで点が下がった
                    ときに「採点が辛くなった」と受け取られないようにする */}
                <p className="mt-1 text-xs leading-relaxed text-subtext">
                  どの当たりでも採点の基準は同じです
                </p>
              </fieldset>
              {notice && (
                <p className="mt-5 rounded-lg border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-on-surface">
                  {notice}
                </p>
              )}
              <button
                type="button"
                onClick={startInterview}
                className="mt-7 inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-lg bg-primary px-5 py-3 font-bold text-white transition-colors hover:bg-primary-hover"
              >
                <Mic size={19} aria-hidden="true" />
                マイクを許可して開始
              </button>
            </div>
          </section>
        ) : (
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1.35fr)_minmax(320px,0.65fr)]">
            <section className="min-w-0 overflow-hidden rounded-lg bg-surface custom-shadow">
              {/* 厳しめは言葉の相槌を禁じているため、映像でもうなずかせない（#1728） */}
              <InterviewerVideoPanel
                phase={phase}
                nodWhileListening={interviewerMode !== "strict"}
              />
              <VoiceConversationStatus
                phase={phase}
                inputLevel={inputLevel}
                isMuted={isMuted}
              />
              {notice && (
                <p className="border-t border-warning/30 bg-warning/10 px-4 py-3 text-sm text-on-surface">
                  {notice}
                </p>
              )}
              <div className="flex min-h-[68px] flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-3">
                <div className="flex items-center gap-4 text-sm font-bold">
                  <span className="inline-flex items-center gap-1.5">
                    <Clock3 size={17} className="text-subtext" />
                    {formatElapsed(elapsedSeconds)} / {formatElapsed(timeLimitSeconds)}
                  </span>
                  <span>回答 {Math.min(answerCount, answerLimit)} / {answerLimit}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    title={isMuted ? "マイクをオンにする" : "マイクをミュートする"}
                    aria-label={isMuted ? "マイクをオンにする" : "マイクをミュートする"}
                    disabled={!isSessionActive}
                    onClick={() => {
                      const nextMuted = !isMuted;
                      setIsMuted(nextMuted);
                      realtimeRef.current?.setMuted(nextMuted);
                    }}
                    className="flex size-11 items-center justify-center rounded-md border border-border bg-white text-on-surface transition-colors hover:bg-surface-container-low disabled:opacity-40"
                  >
                    {isMuted ? <MicOff size={20} /> : <Mic size={20} />}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowEndDialog(true)}
                    disabled={!isSessionActive}
                    className="inline-flex min-h-11 items-center gap-2 rounded-md bg-error px-4 text-sm font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-40"
                  >
                    <LogOut size={18} aria-hidden="true" />
                    終了
                  </button>
                </div>
              </div>
            </section>

            {/* モバイルには高さの上限がなく枠が内容に合わせて伸び続けるため、
                末尾追従がページ全体のスクロールになってしまう。min-h を残したまま
                max-h を足しても min-height が優先されて効かないので、モバイルは
                固定高さにし、min/max は lg 以上だけに残す */}
            <section className="flex h-[360px] flex-col overflow-hidden rounded-lg bg-surface custom-shadow lg:h-auto lg:min-h-[420px] lg:max-h-[680px]">
              <header className="flex items-center justify-between border-b border-border px-4 py-4">
                <h2 className="text-base font-bold">会話ログ</h2>
                <span className="text-xs text-subtext">完了した発話のみ</span>
              </header>
              <div
                ref={transcriptContainerRef}
                data-testid="transcript-scroll-container"
                onScroll={handleTranscriptScroll}
                className="flex-1 space-y-4 overflow-y-auto bg-surface-container-low p-4"
              >
                {turns.length === 0 ? (
                  <div className="flex min-h-[240px] items-center justify-center text-center text-sm text-subtext lg:min-h-[280px]">
                    音声が文字になると、ここに会話が表示されます
                  </div>
                ) : (
                  turns.map((turn) => (
                    <div
                      key={turn.provider_item_id}
                      className={`flex ${
                        turn.role === "candidate"
                          ? "justify-end"
                          : "justify-start"
                      }`}
                    >
                      <div
                        className={`max-w-[88%] rounded-lg px-3 py-2.5 text-sm leading-relaxed ${
                          turn.role === "candidate"
                            ? "bg-primary text-white"
                            : "border border-border bg-white text-on-surface"
                        }`}
                      >
                        <p className={`mb-1 text-xs font-bold ${
                          turn.role === "candidate"
                            ? "text-white/80"
                            : "text-subtext"
                        }`}>
                          {turn.role === "candidate" ? "あなた" : "面接官"}
                        </p>
                        <p>{turn.transcript}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </section>
          </div>
        )}

        {phase === "error" && (
          <section className="mx-auto mt-6 max-w-xl rounded-lg border border-error/30 bg-surface p-5 text-center custom-shadow">
            <p className="text-sm font-bold text-error">{errorMessage}</p>
            <button
              type="button"
              onClick={pendingCompletion ? retryCompletion : startInterview}
              className="mt-4 inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-bold text-white hover:bg-primary-hover"
            >
              <RotateCcw size={17} aria-hidden="true" />
              {pendingCompletion ? "結果生成を再試行" : "もう一度接続"}
            </button>
          </section>
        )}
      </div>

      {showEndDialog && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="quick-interview-end-title"
        >
          <div className="w-full max-w-md rounded-lg bg-white p-6 custom-shadow">
            <div className="mb-4 flex size-11 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Sparkles size={21} aria-hidden="true" />
            </div>
            <h2 id="quick-interview-end-title" className="text-lg font-bold">
              クイック面接を終了しますか？
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-subtext">
              回答済みの内容から結果を生成します。回答がない場合は履歴に残りません。
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowEndDialog(false)}
                className="min-h-11 rounded-lg border border-border px-4 text-sm font-bold text-on-surface hover:bg-surface-container-low"
              >
                続ける
              </button>
              <button
                type="button"
                onClick={() => finishInterview("user_ended")}
                className="min-h-11 rounded-lg bg-error px-4 text-sm font-bold text-white hover:opacity-90"
              >
                終了して結果を見る
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
